import React, { useState, useEffect } from 'react';
import { Navigate, Link } from 'react-router-dom';
import { 
  User, 
  Settings, 
  CreditCard, 
  Shield, 
  Plus, 
  Monitor, 
  Smartphone, 
  Gamepad2, 
  AlertCircle, 
  CheckCircle, 
  Clock,
  Calendar,
  Star,
  Rocket,
  Mail,
  Phone,
  MapPin,
  Edit3,
  Save,
  X,
  Loader2,
  Download,
  ArrowRight
} from 'lucide-react';
import { useAuth } from '../components/auth/AuthProvider';
import { useUserProfile } from '../hooks/useUserProfile';
import { useDevices } from '../hooks/useDevices';
import { DeviceModal } from '../components/DeviceModal';
import LoadingSpinner from '../components/LoadingSpinner';
import { countries } from '../utils/countries';

const MyAccount = () => {
  const { user, signOut, loading: authLoading } = useAuth();
  const { profile, loading: profileLoading, saveProfile } = useUserProfile();
  const { devices, loading: devicesLoading, addDevice, removeDevice } = useDevices();
  
  const [activeTab, setActiveTab] = useState('overview');
  const [isDeviceModalOpen, setIsDeviceModalOpen] = useState(false);
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [profileForm, setProfileForm] = useState({
    first_name: '',
    last_name: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    country: 'United States'
  });
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileError, setProfileError] = useState<string | null>(null);

  // Check for new user flag
  const urlParams = new URLSearchParams(window.location.search);
  const isNewUser = urlParams.get('new') === 'true';

  useEffect(() => {
    if (profile) {
      setProfileForm({
        first_name: profile.first_name || '',
        last_name: profile.last_name || '',
        phone: profile.phone || '',
        address: profile.address || '',
        city: profile.city || '',
        state: profile.state || '',
        zip_code: profile.zip_code || '',
        country: profile.country || 'United States'
      });
    }
  }, [profile]);

  if (authLoading || profileLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 to-emerald-50 flex items-center justify-center pt-16">
        <LoadingSpinner size="lg" text="Loading your account..." />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  const handleSignOut = async () => {
    try {
      await signOut();
    } catch (error) {
      console.error('Sign out error:', error);
    }
  };

  const handleAddDevice = async (deviceData: any) => {
    try {
      const result = await addDevice(deviceData);
      if (result.success) {
        setIsDeviceModalOpen(false);
        return { success: true };
      } else {
        return { success: false, error: result.error };
      }
    } catch (error) {
      return { success: false, error: 'Failed to add device' };
    }
  };

  const handleSaveProfile = async () => {
    setProfileSaving(true);
    setProfileError(null);
    
    try {
      const result = await saveProfile(profileForm);
      if (result.success) {
        setIsEditingProfile(false);
      } else {
        setProfileError(result.error || 'Failed to save profile');
      }
    } catch (error) {
      setProfileError('An unexpected error occurred');
    } finally {
      setProfileSaving(false);
    }
  };

  const getDeviceIcon = (type: string) => {
    switch (type) {
      case 'pc': return Monitor;
      case 'console': return Gamepad2;
      case 'mobile': return Smartphone;
      default: return Monitor;
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: User },
    { id: 'subscription', label: 'Subscription', icon: CreditCard },
    { id: 'devices', label: 'Devices', icon: Shield },
    { id: 'profile', label: 'Profile', icon: Settings },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 to-emerald-50 pt-16">
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Welcome Header */}
        <div className="mb-8">
          <div className="bg-white rounded-xl shadow-lg border border-teal-100 p-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h1 className="text-2xl font-bold text-teal-900 mb-2">
                  Welcome back{profile?.first_name ? `, ${profile.first_name}` : ''}!
                </h1>
                <p className="text-slate-600">
                  Manage your SafePlay AI account and family protection settings
                </p>
              </div>
              <button
                onClick={handleSignOut}
                className="mt-4 sm:mt-0 px-4 py-2 text-slate-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
              >
                Sign Out
              </button>
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="mb-8">
          <div className="bg-white rounded-xl shadow-lg border border-teal-100 p-2">
            <div className="flex flex-wrap gap-2">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center space-x-2 px-4 py-3 rounded-lg font-medium transition-all ${
                      activeTab === tab.id
                        ? 'bg-teal-600 text-white shadow-md'
                        : 'text-slate-600 hover:text-teal-600 hover:bg-teal-50'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    <span>{tab.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Tab Content */}
        <div className="space-y-8">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="grid lg:grid-cols-2 gap-8">
              {/* Subscription Status */}
              <div className="bg-white rounded-xl shadow-lg border border-teal-100 p-6">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-10 h-10 bg-teal-100 rounded-xl flex items-center justify-center">
                    <CreditCard className="w-5 h-5 text-teal-600" />
                  </div>
                  <h3 className="text-xl font-bold text-teal-900">Subscription Status</h3>
                </div>
                
                <div className="space-y-4">
                  <div className="flex items-center space-x-2">
                    <Clock className="w-5 h-5 text-orange-500" />
                    <span className="font-semibold text-orange-700">Coming Soon</span>
                  </div>
                  <p className="text-slate-600">
                    SafePlay AI is currently in development. Stay tuned for launch updates!
                  </p>
                  <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                    <div className="flex items-center space-x-2 mb-2">
                      <Rocket className="w-4 h-4 text-emerald-600" />
                      <span className="font-semibold text-emerald-900">Expected Launch: March 2025</span>
                    </div>
                    <p className="text-emerald-800 text-sm">
                      We're working hard to bring you the best gaming safety platform for families.
                    </p>
                  </div>
                </div>
              </div>

              {/* Device Summary */}
              <div className="bg-white rounded-xl shadow-lg border border-teal-100 p-6">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center">
                    <Shield className="w-5 h-5 text-emerald-600" />
                  </div>
                  <h3 className="text-xl font-bold text-emerald-900">Protected Devices</h3>
                </div>
                
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-slate-600">Registered Devices</span>
                    <span className="font-semibold text-slate-900">
                      {devices.length} / 0
                    </span>
                  </div>
                  
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-center space-x-2 mb-2">
                      <Clock className="w-4 h-4 text-blue-600" />
                      <span className="font-semibold text-blue-900">Device Setup Available at Launch</span>
                    </div>
                    <p className="text-blue-800 text-sm">
                      You'll be able to add and configure devices once SafePlay AI launches.
                    </p>
                  </div>
                  
                  {devices.length === 0 ? (
                    <div className="text-center py-4">
                      <p className="text-slate-500 mb-4">No devices registered yet</p>
                      <button
                        onClick={() => setActiveTab('devices')}
                        className="text-teal-600 hover:text-teal-700 font-medium"
                      >
                        Add your first device →
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {devices.slice(0, 3).map((device) => {
                        const Icon = getDeviceIcon(device.type);
                        return (
                          <div key={device.id} className="flex items-center space-x-3 p-2 bg-gray-50 rounded-lg">
                            <Icon className="w-4 h-4 text-slate-600" />
                            <span className="text-sm text-slate-700">{device.name}</span>
                          </div>
                        );
                      })}
                      {devices.length > 3 && (
                        <p className="text-xs text-slate-500 text-center">
                          +{devices.length - 3} more devices
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Subscription Tab */}
          {activeTab === 'subscription' && (
            <div className="bg-white rounded-xl shadow-lg border border-teal-100 p-6">
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-10 h-10 bg-teal-100 rounded-xl flex items-center justify-center">
                  <CreditCard className="w-5 h-5 text-teal-600" />
                </div>
                <h3 className="text-xl font-bold text-teal-900">Current Subscription</h3>
              </div>

              {/* Show purchase error if exists */}
              {purchaseError && (
                <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-center space-x-2 text-red-800">
                    <AlertCircle className="w-4 h-4" />
                    <span className="text-sm font-medium">Purchase Status Error</span>
                  </div>
                  <p className="text-sm text-red-600 mt-1">{purchaseError}</p>
                </div>
              )}
              
              {/* Show purchase status based on actual database records */}
              {purchaseStatus?.hasPurchased ? (
                /* Pre-Order Display */
                <div className="space-y-6">
                  <div className="bg-gradient-to-r from-orange-50 to-amber-50 border border-orange-200 rounded-xl p-6">
                    <div className="flex items-center space-x-3 mb-4">
                      <Star className="w-6 h-6 text-orange-500" />
                      <h4 className="text-xl font-bold text-orange-900">Pre-Order Confirmed</h4>
                    </div>
                    
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <h5 className="font-semibold text-orange-900 mb-2">Plan Details</h5>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-orange-800">Plan:</span>
                            <span className="font-medium text-orange-900">{subscriptionStatus.planName}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-orange-800">Type:</span>
                            <span className="font-medium text-orange-900">One-time purchase</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-orange-800">Protection Period:</span>
                            <span className="font-medium text-orange-900">6 months</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-orange-800">Max Devices:</span>
                            <span className="font-medium text-orange-900">{subscriptionStatus.maxDevices}</span>
                          </div>
                          {subscriptionStatus.customerId && (
                            <div className="flex justify-between">
                              <span className="text-orange-800">Customer ID:</span>
                              <span className="font-medium text-orange-900 font-mono text-xs">{subscriptionStatus.customerId}</span>
                            </div>
                          )}
                        </div>
                      </div>
                      
                      <div>
                        <h5 className="font-semibold text-orange-900 mb-2">Pre-Order Status</h5>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-orange-800">Status:</span>
                            <span className="font-medium text-orange-900">Pre-Order Confirmed</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-orange-800">Activation:</span>
                            <span className="font-medium text-orange-900">At app launch</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="mt-6 p-4 bg-white/60 rounded-lg">
                      <h5 className="font-semibold text-orange-900 mb-2">What Happens Next?</h5>
                      <ul className="space-y-1 text-sm text-orange-800">
                        <li>• Weekly progress updates via email</li>
                        <li>• Early access when SafePlay AI launches</li>
                        <li>• 6 months of protection starting from launch</li>
                        <li>• Option to renew or upgrade after 6 months</li>
                      </ul>
                    </div>
                  </div>

                  {/* Renewal Information */}
                  <div className="bg-teal-50 border border-teal-200 rounded-xl p-6">
                    <div className="flex items-center space-x-3 mb-4">
                      <Calendar className="w-5 h-5 text-teal-600" />
                      <h4 className="text-lg font-bold text-teal-900">After Your 6 Months</h4>
                    </div>
                    <p className="text-teal-800 mb-4">
                      Your {purchaseStatus.planName || 'purchase'} is secured and will activate when SafePlay AI launches.
                      you can renew or choose from any available subscription plans.
                    </p>
                    <Link
                      to="/take-action"
                      className="inline-flex items-center space-x-2 text-teal-600 hover:text-teal-700 font-medium"
                    >
                      <span>View renewal options</span>
                      <ArrowRight className="w-4 h-4" />
                    </Link>
                  </div>
                  
                  {/* Purchase Details */}
                  <div className="bg-teal-50 border border-teal-200 rounded-lg p-4">
                    <h4 className="font-semibold text-teal-900 mb-2">Purchase Details</h4>
                    <div className="space-y-1 text-sm">
                      {purchaseStatus.purchaseDate && (
                        <div className="flex justify-between">
                          <span className="text-teal-800">Purchase Date:</span>
                          <span className="font-medium text-teal-900">
                            {new Date(purchaseStatus.purchaseDate).toLocaleDateString()}
                          </span>
                        </div>
                      )}
                      {purchaseStatus.customerId && (
                        <div className="flex justify-between">
                          <span className="text-teal-800">Customer ID:</span>
                          <span className="font-medium text-teal-900 font-mono text-xs">
                            {purchaseStatus.customerId}
                          </span>
                        </div>
                      )}
                      <div className="flex justify-between">
                        <span className="text-teal-800">Status:</span>
                        <span className="font-medium text-teal-900">Active Pre-Order</span>
                      </div>
                    </div>
                  </div>
                </div>
              ) : subscriptionStatus?.hasActiveSubscription ? (
                /* Active Subscription Display */
                <div className="space-y-6">
                  <div className="bg-green-50 border border-green-200 rounded-xl p-6">
                    <div className="flex items-center space-x-3 mb-4">
                      <CheckCircle className="w-6 h-6 text-green-500" />
                      <h4 className="text-xl font-bold text-green-900">Active Protection</h4>
                    </div>
                    
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <h5 className="font-semibold text-green-900 mb-2">Plan Details</h5>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-green-800">Plan:</span>
                            <span className="font-medium text-green-900">{subscriptionStatus.planName}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-green-800">Status:</span>
                            <span className="font-medium text-green-900 capitalize">{subscriptionStatus.status}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-green-800">Max Devices:</span>
                            <span className="font-medium text-green-900">{subscriptionStatus.maxDevices}</span>
                          </div>
                        </div>
                      </div>
                      
                      <div>
                        <h5 className="font-semibold text-green-900 mb-2">Billing</h5>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-green-800">Next Billing:</span>
                            <span className="font-medium text-green-900">
                              {subscriptionStatus.currentPeriodEnd ? formatDate(subscriptionStatus.currentPeriodEnd) : 'N/A'}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                /* No Subscription */
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <AlertCircle className="w-8 h-8 text-teal-600" />
                  </div>
                  <h4 className="text-xl font-bold text-slate-900 mb-4">No Active Subscription</h4>
                  <p className="text-slate-600 mb-6">
                    Start protecting your family's gaming experience with SafePlay AI.
                  </p>
                  
                  {/* Debug Information for Development */}
                  {process.env.NODE_ENV === 'development' && (
                    <div className="mb-6 p-4 bg-gray-50 border border-gray-200 rounded-lg text-left">
                      <h5 className="font-semibold text-gray-900 mb-2">Debug Info (Dev Only)</h5>
                      <div className="text-xs text-gray-600 space-y-1">
                        <div>User Email: {user?.email}</div>
                        <div>Purchase Status: {purchaseStatus ? JSON.stringify(purchaseStatus, null, 2) : 'Loading...'}</div>
                        <div>Subscription Status: {subscriptionStatus ? JSON.stringify(subscriptionStatus, null, 2) : 'Loading...'}</div>
                      </div>
                    </div>
                  )}
                  
                  <Link
                    to="/take-action"
                    className="inline-flex items-center space-x-2 bg-teal-600 text-white px-6 py-3 rounded-lg hover:bg-teal-700 transition-colors"
                  >
                    <span>Choose a Plan</span>
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              )}
            </div>
          )}

          {/* Devices Tab */}
          {activeTab === 'devices' && (
            <div className="bg-white rounded-xl shadow-lg border border-teal-100 p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center">
                    <Shield className="w-5 h-5 text-emerald-600" />
                  </div>
                  <h3 className="text-xl font-bold text-emerald-900">Protected Devices</h3>
                </div>
                
                {!subscriptionStatus?.isPreOrder && (
                  <button
                    onClick={() => setIsDeviceModalOpen(true)}
                    disabled={!subscriptionStatus?.hasActiveSubscription || devices.length >= (subscriptionStatus?.maxDevices || 0)}
                    className="flex items-center space-x-2 bg-emerald-600 text-white px-4 py-2 rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Plus className="w-4 h-4" />
                    <span>Add Device</span>
                  </button>
                )}
              </div>

              {subscriptionStatus?.isPreOrder ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Download className="w-8 h-8 text-teal-600" />
                  </div>
                  <h4 className="text-xl font-bold text-teal-900 mb-4">Device Setup Coming Soon</h4>
                  <p className="text-teal-800 mb-6 max-w-md mx-auto">
                    Once SafePlay AI launches, you'll be able to download the app and set up monitoring on up to {subscriptionStatus?.maxDevices || purchaseStatus?.isPreOrder ? 2 : 0} devices.
                  </p>
                  <div className="bg-teal-50 border border-teal-200 rounded-lg p-4 max-w-md mx-auto">
                    <p className="text-teal-800 text-sm">
                      <strong>Your protection credit is secured!</strong> You'll receive download instructions and setup guides when we launch.
                    </p>
                  </div>
                </div>
              ) : devices.length === 0 ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Shield className="w-8 h-8 text-slate-400" />
                  </div>
                  <h4 className="text-xl font-bold text-slate-900 mb-4">No Devices Added Yet</h4>
                  <p className="text-slate-600 mb-6">
                    Add your child's gaming devices to start monitoring and protection.
                  </p>
                  {subscriptionStatus?.hasActiveSubscription ? (
                    <button
                      onClick={() => setIsDeviceModalOpen(true)}
                      className="inline-flex items-center space-x-2 bg-emerald-600 text-white px-6 py-3 rounded-lg hover:bg-emerald-700 transition-colors"
                    >
                      <Plus className="w-4 h-4" />
                      <span>Add First Device</span>
                    </button>
                  ) : (
                    <Link
                      to="/take-action"
                      className="inline-flex items-center space-x-2 bg-teal-600 text-white px-6 py-3 rounded-lg hover:bg-teal-700 transition-colors"
                    >
                      <span>Get Started</span>
                      <ArrowRight className="w-4 h-4" />
                    </Link>
                  )}
                </div>
              ) : (
                <div className="space-y-4">
                  {devices.map((device) => {
                    const Icon = getDeviceIcon(device.type);
                    return (
                      <div key={device.id} className="border border-gray-200 rounded-lg p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <Icon className="w-6 h-6 text-slate-600" />
                            <div>
                              <h4 className="font-semibold text-slate-900">{device.name}</h4>
                              <p className="text-sm text-slate-600">
                                {device.operating_system} • Child: {device.child_name} ({device.child_age} years old)
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              device.status === 'online' ? 'bg-green-100 text-green-800' :
                              device.status === 'gaming' ? 'bg-blue-100 text-blue-800' :
                              device.status === 'paused' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {device.status}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Profile Tab */}
          {activeTab === 'profile' && (
            <div className="bg-white rounded-xl shadow-lg border border-teal-100 p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-slate-100 rounded-xl flex items-center justify-center">
                    <User className="w-5 h-5 text-slate-600" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900">Profile Information</h3>
                </div>
                
                {!isEditingProfile && (
                  <button
                    onClick={() => setIsEditingProfile(true)}
                    className="flex items-center space-x-2 text-teal-600 hover:text-teal-700 font-medium"
                  >
                    <Edit3 className="w-4 h-4" />
                    <span>Edit</span>
                  </button>
                )}
              </div>

              {profileError && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start space-x-3">
                  <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm text-red-700 font-medium">Error</p>
                    <p className="text-sm text-red-600">{profileError}</p>
                  </div>
                </div>
              )}

              {isEditingProfile ? (
                <form className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        First Name
                      </label>
                      <input
                        type="text"
                        value={profileForm.first_name}
                        onChange={(e) => setProfileForm(prev => ({ ...prev, first_name: e.target.value }))}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                        placeholder="Enter your first name"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Last Name
                      </label>
                      <input
                        type="text"
                        value={profileForm.last_name}
                        onChange={(e) => setProfileForm(prev => ({ ...prev, last_name: e.target.value }))}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                        placeholder="Enter your last name"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      value={profileForm.phone}
                      onChange={(e) => setProfileForm(prev => ({ ...prev, phone: e.target.value }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                      placeholder="Enter your phone number"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Address
                    </label>
                    <input
                      type="text"
                      value={profileForm.address}
                      onChange={(e) => setProfileForm(prev => ({ ...prev, address: e.target.value }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                      placeholder="Enter your address"
                    />
                  </div>

                  <div className="grid md:grid-cols-3 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        City
                      </label>
                      <input
                        type="text"
                        value={profileForm.city}
                        onChange={(e) => setProfileForm(prev => ({ ...prev, city: e.target.value }))}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                        placeholder="City"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        State
                      </label>
                      <input
                        type="text"
                        value={profileForm.state}
                        onChange={(e) => setProfileForm(prev => ({ ...prev, state: e.target.value }))}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                        placeholder="State"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        ZIP Code
                      </label>
                      <input
                        type="text"
                        value={profileForm.zip_code}
                        onChange={(e) => setProfileForm(prev => ({ ...prev, zip_code: e.target.value }))}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                        placeholder="ZIP"
                      />
                    </div>
                  </div>

                  <div className="flex space-x-3">
                    <button
                      type="button"
                      onClick={() => {
                        setIsEditingProfile(false);
                        setProfileError(null);
                      }}
                      className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleSaveProfile}
                      disabled={profileSaving}
                      className="flex-1 px-4 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors disabled:opacity-50 flex items-center justify-center space-x-2"
                    >
                      {profileSaving ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          <span>Saving...</span>
                        </>
                      ) : (
                        <>
                          <Save className="w-4 h-4" />
                          <span>Save Changes</span>
                        </>
                      )}
                    </button>
                  </div>
                </form>
              ) : (
                <div className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <div className="flex items-center space-x-3 p-4 bg-gray-50 rounded-lg">
                        <User className="w-5 h-5 text-slate-600" />
                        <div>
                          <p className="text-sm text-slate-600">Name</p>
                          <p className="font-medium text-slate-900">
                            {profile?.first_name || profile?.last_name 
                              ? `${profile.first_name || ''} ${profile.last_name || ''}`.trim()
                              : 'Not provided'
                            }
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-3 p-4 bg-gray-50 rounded-lg">
                        <Mail className="w-5 h-5 text-slate-600" />
                        <div>
                          <p className="text-sm text-slate-600">Email</p>
                          <p className="font-medium text-slate-900">{user.email}</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-3 p-4 bg-gray-50 rounded-lg">
                        <Phone className="w-5 h-5 text-slate-600" />
                        <div>
                          <p className="text-sm text-slate-600">Phone</p>
                          <p className="font-medium text-slate-900">{profile?.phone || 'Not provided'}</p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="space-y-4">
                      <div className="flex items-start space-x-3 p-4 bg-gray-50 rounded-lg">
                        <MapPin className="w-5 h-5 text-slate-600 mt-1" />
                        <div>
                          <p className="text-sm text-slate-600">Address</p>
                          <div className="font-medium text-slate-900">
                            {profile?.address ? (
                              <div>
                                <p>{profile.address}</p>
                                <p>
                                  {[profile.city, profile.state, profile.zip_code].filter(Boolean).join(', ')}
                                </p>
                                <p>{profile.country}</p>
                              </div>
                            ) : (
                              'Not provided'
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Device Modal */}
      <DeviceModal
        isOpen={isDeviceModalOpen}
        onClose={() => setIsDeviceModalOpen(false)}
        onSubmit={handleAddDevice}
        maxDevices={subscriptionStatus?.maxDevices || 0}
        currentDeviceCount={devices.length}
      />

      {/* Purchase Notification */}
      {subscriptionStatus && (
        <PurchaseNotification
          isVisible={showPurchaseNotification}
          onClose={() => setShowPurchaseNotification(false)}
          planName={subscriptionStatus.planName || 'Unknown Plan'}
          isPreOrder={subscriptionStatus.isPreOrder}
        />
      )}
    </div>
  );
};

export default MyAccount;